import { isPaymentListingRowReconcilable } from '@blocktickets/shared-utils'
import {
  getEventDateForOrder,
  getEventNameForOrder,
} from '../../utilities/helpers'
import { getFacilityFee } from '../DailySettlementWrapper/getFacilityFee'
import {
  buildDailySettlementDataFromListings,
  getDailySettlementRefundAmount,
  getSettlementAdditionalFees,
  getSettlementChargebackProtection,
  getSettlementDonationTotal,
  getSettlementGross,
  getSettlementNet,
  getSettlementProcessingFee,
  getSettlementSalesTax,
  getSettlementServiceFee,
  isCompleteOrder,
  isRefundedOrder,
} from '../DailySettlementWrapper/dailySettlementData'

const toMoney = (value) => {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

const payoutAmountDollars = (payout) => toMoney(payout?.amount) / 100

/**
 * Stripe payouts created in the given calendar year (browser local).
 */
export function getYearToDatePayouts(
  payouts = [],
  year = new Date().getFullYear()
) {
  return (payouts || []).filter((payout) => {
    const created = Number(payout?.created)
    if (!Number.isFinite(created)) return false
    return new Date(created * 1000).getFullYear() === year
  })
}

export function sumPayoutAmounts(payouts = []) {
  return (payouts || []).reduce(
    (sum, payout) => sum + payoutAmountDollars(payout),
    0
  )
}

/** Most recent payout by Stripe `created` timestamp. */
export function getMostRecentPayout(payouts = []) {
  if (!payouts?.length) return null
  return [...payouts].sort(
    (a, b) => Number(b?.created || 0) - Number(a?.created || 0)
  )[0]
}

/**
 * Aggregate payment-listings into accounting dashboard KPIs and section data.
 */
export function buildAccountingDashboardData(apiRows = []) {
  const { raw, byPaymentMethod } = buildDailySettlementDataFromListings(apiRows)

  let grossSales = 0
  let serviceFees = 0
  let facilityFees = 0
  let customFees = 0
  let processingFees = 0
  let chargebackFees = 0
  let salesTax = 0
  let donationTotals = 0
  let netSalesComplete = 0
  let refunds = 0
  let refundCount = 0
  let completeOrderCount = 0
  let ticketsSold = 0
  let comps = 0
  let reconcilableCount = 0
  let totalReviewableCount = 0

  const byEvent = {}

  raw.forEach((order) => {
    const refundAmount = getDailySettlementRefundAmount(order)
    if (refundAmount > 0) {
      refunds += refundAmount
      refundCount += 1
    }

    totalReviewableCount += 1
    if (isPaymentListingRowReconcilable(order)) {
      reconcilableCount += 1
    }

    if (!isCompleteOrder(order) || isRefundedOrder(order)) return

    completeOrderCount += 1
    const quantity = toMoney(order?.quantity || 1)
    ticketsSold += quantity

    const paymentMethod = String(order?.paymentMethod || '')
      .trim()
      .toUpperCase()
    if (paymentMethod === 'COMP' || paymentMethod === 'COMPLIMENTARY') {
      comps += quantity
    }

    const gross = getSettlementGross(order)
    const serviceFee = getSettlementServiceFee(order)
    const facilityFee = getFacilityFee(order)
    const customFee = getSettlementAdditionalFees(order)
    const processingFee = getSettlementProcessingFee(order)
    const chargebackFee = getSettlementChargebackProtection(order)
    const tax = getSettlementSalesTax(order)
    const donation = getSettlementDonationTotal(order)
    const net = getSettlementNet(order)
    const orderFees =
      serviceFee +
      processingFee +
      chargebackFee +
      facilityFee +
      customFee +
      donation +
      tax

    grossSales += gross
    serviceFees += serviceFee
    facilityFees += facilityFee
    customFees += customFee
    processingFees += processingFee
    chargebackFees += chargebackFee
    salesTax += tax
    donationTotals += donation
    netSalesComplete += net

    const eventKey =
      order?.eventUuid ||
      order?.eventId ||
      getEventNameForOrder(order) ||
      'unknown'
    if (!byEvent[eventKey]) {
      byEvent[eventKey] = {
        eventKey,
        eventName: getEventNameForOrder(order),
        venueName: order?.venueName || order?.organizationName || '',
        eventDate: getEventDateForOrder(order, order?.timezone),
        tickets: 0,
        comps: 0,
        gross: 0,
        fees: 0,
        facilityFees: 0,
        net: 0,
      }
    }
    byEvent[eventKey].tickets += quantity
    if (paymentMethod === 'COMP' || paymentMethod === 'COMPLIMENTARY') {
      byEvent[eventKey].comps += quantity
    }
    byEvent[eventKey].gross += gross
    byEvent[eventKey].fees += orderFees
    byEvent[eventKey].facilityFees += facilityFee
    byEvent[eventKey].net += net
  })

  const feesCollected =
    serviceFees +
    processingFees +
    chargebackFees +
    facilityFees +
    customFees +
    donationTotals +
    salesTax
  const netSales = grossSales - feesCollected - refunds
  const needsReviewCount = Math.max(0, totalReviewableCount - reconcilableCount)
  const reconciliationPct =
    totalReviewableCount > 0
      ? (reconcilableCount / totalReviewableCount) * 100
      : 100

  const feeBreakdown = [
    { key: 'custom', label: 'Custom fees', amount: customFees },
    { key: 'processing', label: 'Processing fees', amount: processingFees },
    { key: 'facility', label: 'Facility fees', amount: facilityFees },
    ...(salesTax > 0
      ? [{ key: 'tax', label: 'Sales tax', amount: salesTax }]
      : []),
    ...(donationTotals > 0
      ? [{ key: 'donation', label: 'Donations', amount: donationTotals }]
      : []),
  ]
  const feeBreakdownTotal = feeBreakdown.reduce(
    (sum, row) => sum + row.amount,
    0
  )

  return {
    raw,
    byPaymentMethod,
    byEvent: Object.values(byEvent).sort((a, b) => b.gross - a.gross),
    kpis: {
      grossSales,
      feesCollected,
      feePctOfGross: grossSales > 0 ? (feesCollected / grossSales) * 100 : 0,
      refunds,
      refundCount,
      netSales,
      netSalesComplete,
      completeOrderCount,
      ticketsSold,
      comps,
      reconciliationPct,
      needsReviewCount,
      totalReviewableCount,
    },
    feeBreakdown: feeBreakdown.map((row) => ({
      ...row,
      pct: feeBreakdownTotal > 0 ? (row.amount / feeBreakdownTotal) * 100 : 0,
    })),
    feesByPaymentMethod: byPaymentMethod.map((row) => ({
      paymentMethod: row.paymentMethod,
      fees:
        toMoney(row.serviceFees) +
        toMoney(row.processingFees) +
        toMoney(row.chargebackFees) +
        toMoney(row.facilityFees) +
        toMoney(row.additionalFees) +
        toMoney(row.donationTotals) +
        toMoney(row.salesTax),
      orderCount: row.orderCount,
      grossSales: row.grossSales,
      netSales: row.netSales,
    })),
  }
}
